import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { acceptAlert, listAlerts, markAlertRead } from '@/lib/alertsApi'
import type { AlertListFilters } from '@/types/alert'

const ALERTS_KEY = ['alerts'] as const

// 30 s ist die Polling-Frequenz aus dem Session-Plan: schnell genug, dass
// neue Alerts vom Scheduler innerhalb einer halben Minute sichtbar werden,
// langsam genug, um nicht in jedem Tick die DB zu treffen.
const BADGE_POLL_MS = 30_000

/**
 * Globaler Badge-Hook: lädt nur den Unread-Count via leichter List-Query
 * mit limit=1, polled alle 30 s. Verwendet im Header.
 */
export function useUnreadAlertCount() {
  return useQuery({
    queryKey: [...ALERTS_KEY, 'unread-count'],
    queryFn: () => listAlerts({ limit: 1 }),
    refetchInterval: BADGE_POLL_MS,
    refetchIntervalInBackground: false,
    staleTime: 0,
    select: (data) => data.unread_count,
  })
}

/**
 * Listing-Hook für /alerts-Page und Dashboard-Widget. Filter-Parameter
 * werden in den Query-Key gehängt — Wechsel auf "Nur ungelesen" macht
 * eine neue Cache-Entry und kein flackerndes Re-Render.
 */
export function useAlertList(filters: AlertListFilters = {}) {
  return useQuery({
    queryKey: [...ALERTS_KEY, 'list', filters],
    queryFn: () => listAlerts(filters),
    staleTime: 10_000,
  })
}

export function useMarkAlertRead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: markAlertRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ALERTS_KEY })
    },
  })
}

export function useAcceptAlert() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: acceptAlert,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ALERTS_KEY })
    },
  })
}
